import * as React from 'react';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {TextRegularAiIcon} from 'shells/icon/svg/text-regular-ai-icon';
import {IconSection} from 'shells/icon-section/icon-section';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import type {
    EntitySummaryStatus,
    ActivityTranscriptionStatus,
    EntitySummaryRating,
} from 'nutshell-graphql-types';
import type {
    GetActivityForModal_activity_summary as EntitySummary,
    GetActivityForModal_activity_tasks as ActivityTask,
} from 'nutshell-graphql-types';

import {useRateEntitySummary} from '../../../power-ai/timeline-summary/graphql/hooks/use-rate-entity-summary';

import {analyticsClient} from '../../../../analytics-client';

import {useDeleteTranscriptSummary, useActivitySummarize} from '../../graphql/hooks';

import {TranscriptSummaryContent} from '.';

type Props = {
    activityId: string;
    summary: EntitySummary | null | undefined;
    summaryStatus: EntitySummaryStatus | null | undefined;
    summaryState: string;
    summaryNextAction: string;
    canViewSummaryDetails: boolean;
    handlePollForSummaryAndTranscript: () => Promise<any>;
    errorMessage: string | null | undefined;
    setErrorMessage: (errorMessage: string) => void;
    generatingSummary: boolean;
    transcriptionStatus: ActivityTranscriptionStatus | null | undefined;
    isNotetakerDisabled: boolean;
    tasks?: ActivityTask[];
};

export function TranscriptSummary(props: Props) {
    const [isGeneratingSummary, setIsGeneratingSummary] = React.useState<boolean>(false);
    const [isDeletingSummary, setIsDeletingSummary] = React.useState<boolean>(false);

    const {deleteTranscriptSummary} = useDeleteTranscriptSummary();
    const {activitySummarize} = useActivitySummarize();
    const {rateEntitySummary, isSavingRating} = useRateEntitySummary();

    const shouldPollForSummary =
        (props.summaryStatus === 'LOADING' || props.generatingSummary) &&
        !isGeneratingSummary &&
        props.canViewSummaryDetails &&
        props.summaryNextAction !== 'UPGRADE' &&
        props.transcriptionStatus !== 'NOT_ENABLED';

    if (shouldPollForSummary) {
        setIsGeneratingSummary(true);
        props
            .handlePollForSummaryAndTranscript()
            .then(() => {
                setIsGeneratingSummary(false);
            })
            .catch((error) => {
                setIsGeneratingSummary(false);
                props.setErrorMessage(error.message);
            });
    }

    const handleSummarize = (): Promise<any> => {
        if (isGeneratingSummary) return Promise.resolve();

        setIsGeneratingSummary(true);

        return activitySummarize({
            activityId: props.activityId,
        })
            .then(() => {
                props
                    .handlePollForSummaryAndTranscript()
                    .then(() => {
                        setIsGeneratingSummary(false);
                    })
                    .catch((error) => {
                        setIsGeneratingSummary(false);
                        props.setErrorMessage(error.message);
                    });
                analyticsClient.track(
                    analyticsClient.EVENTS.TRACK_ACTIVITY_SUMMARY_GENERATED_VIA_MODAL
                );
            })
            .catch(() => {
                setIsGeneratingSummary(false);
                props.setErrorMessage('Error generating summary, please refresh and try again.');
            });
    };

    const handleDeleteSummary = () => {
        setIsDeletingSummary(true);
        deleteTranscriptSummary({
            activityId: props.activityId,
        })
            .catch(() => {
                props.setErrorMessage(
                    'Error removing summary, please refresh and try again, or contact support.'
                );
            })
            .finally(() => {
                setIsDeletingSummary(false);
            });
    };

    const handleRateSummary = (rating: EntitySummaryRating): Promise<any> => {
        return props.summary
            ? rateEntitySummary({entitySummaryId: props.summary.id, rating})
            : Promise.resolve();
    };

    return (
        <>
            <IconSection
                icon={TextRegularAiIcon}
                iconColor={colors.plum}
                fullWidth={true}
                marginTopValue={2}
            >
                <StackLegacy spacing={8}>
                    <div className='flex justify-sb'>
                        <Body size='small' color='plum'>
                            Summary
                        </Body>
                        {props.summaryNextAction === 'SUMMARIZE' && !props.isNotetakerDisabled && (
                            <Button
                                variant='text-primary'
                                noPadding={true}
                                onClick={handleSummarize}
                                disabled={isGeneratingSummary}
                            >
                                Try again
                            </Button>
                        )}
                    </div>
                    <TranscriptSummaryContent
                        activityId={props.activityId}
                        summary={props.summary && props.summary.summary}
                        isDeletingSummary={isDeletingSummary}
                        isGeneratingSummary={isGeneratingSummary}
                        canViewSummaryDetails={props.canViewSummaryDetails}
                        summaryNextAction={props.summaryNextAction}
                        summaryState={props.summaryState}
                        isNotetakerDisabled={props.isNotetakerDisabled}
                        userRating={props.summary && props.summary.userRating}
                        onRate={handleRateSummary}
                        isSavingRating={isSavingRating}
                        onRefresh={
                            props.summary && props.canViewSummaryDetails
                                ? handleSummarize
                                : undefined
                        }
                        tasks={props.tasks}
                        // @ts-expect-error
                        onDelete={
                            props.summary &&
                            props.canViewSummaryDetails &&
                            !isDeletingSummary &&
                            !isGeneratingSummary &&
                            !props.errorMessage
                                ? handleDeleteSummary
                                : undefined
                        }
                    />
                </StackLegacy>
            </IconSection>
        </>
    );
}
